import { HeartIcon, CalendarIcon, CurrencyEuroIcon } from "@heroicons/react/24/solid";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "../../components/atoms/Button";
import Input from "../../components/atoms/Input";
import greenShape from "../../theme/images/registerGreenShape.png";
import purpleShape from "../../theme/images/registerPurpleShape.png";
import { trpc } from "../../utils/trpc";
import styles from "./PatientRegister.module.scss";
import routes from "routes";
import { getToken } from "utils/getToken";
import { useFormik } from "formik";
import * as yup from "yup";
import { Alert } from "antd";
import { analyticsService } from "../../utils/analyticsService";
import supporto from "../../theme/images/supporto.webp";
import selfLove from '../../theme/images/self_love.webp';
import nutritionist from '../../theme/images/nutritionist.webp';
import { envs } from "../../utils/envs";
import { raiseException } from "../../utils/raiseException";
import trimString from "../../utils/trimString";
import { useTherapistData } from '../../customHooks/therapistDataContext';
import { isDevEnvironment } from '../../utils/general';
import { createIubendaConsent } from '../../utils/iubendaAPI';
import FormHeader from '../../components/atoms/FormHeader';
import Modal from '../../components/organisms/Modal';
import { internationalPrefix } from "../../utils/international_prefix";

const appSiteUrl = envs.REACT_APP_SITE_URL;

export const PatientRegister = ({className}: any) => {
	const navigate = useNavigate();
	const location = useLocation();
	let {state} = location;
	const {removeTherapistData} = useTherapistData();
	const gender = state?.gender ? state.gender : "other";
	const [registerLoading, setRegisterLoading] = useState(false);
	const [registerError, setRegisterError] = useState<string | null>();
	const [passwordTypeToggle, setPasswordTypeToggle] = useState(true);
	const [answers, setAnswers] = useState<any>([]);
	const [acceptConditions, setAcceptConditions] = useState<boolean>(false);
	const [marketing, setMarketing] = useState<boolean>(false);
	const [newsLetter, setNewsLetter] = useState(false);
	const [soleGuardianship, setSoleGuardianship] = useState(false);
	const [formData, setFormData] = useState<string>('');
	const [nutritionistModal, setNutritionistModal] = useState<boolean>(false);
	const [phonePrefix, setPhonePrefix] = useState('+39');
	const [phoneNumber, setPhoneNumber] = useState('');

	const formRef = useRef<HTMLFormElement | null>(null);

	const isNutritionist = state?.answers.id === '0597b2cc-b99d-4347-9c18-9da93e7a867e';
	const isPsychiatry = state?.answers.id === 'df5fbfdd-db8f-42d7-bbbd-23533aa7d476';
	const isCouple = state?.answers.id === '762bbb63-a571-44ab-a135-607e4ae07968';
	const isMinori = state?.answers.id === 'a3b8aa4f-aebb-477e-a8c6-f07739b9e108';

	const privacyLink = answers?.id !== 'df5fbfdd-db8f-42d7-bbbd-23533aa7d476' ? 'https://mymentis.it/privacy-paziente/' : 'https://mymentis.it/privacy-policy-paziente-psc/';
	const generalPrivacyLink = 'https://mymentis.it/privacy-generale/';

	useEffect(() => {
		if (formRef.current?.outerHTML && !formData) {
			setFormData(formRef.current.outerHTML);
		}
	}, []);

	const formik = useFormik({
		initialValues: {
			givenName: '',
			email: '',
			confirmEmail: '',
			emailPartner: '',
			emailParent: '',
			otherParentEmail: '',
			password: '',
			acceptConditions: false,
			marketing: false,
			soleGuardianship: false,
			phoneNumber: '',
		},
		validationSchema: yup.object().shape({
			givenName: yup
				.string()
				.min(3, 'Il nome deve contenere almeno 3 caratteri')
				.max(50, 'Il nome non può superare i 50 caratteri')
				.required('Il nome è un campo obbligatorio'),
			email: yup
				.string()
				.trim()
				.matches(
					/^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
					"L'email inserita ha un formato non valido"
				)
				.required("L'email è un campo obbligatorio"),
			emailPartner: yup
				.string()
				.trim()
				.matches(
					/^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
					"L'email inserita ha un formato non valido"
				),
			emailParent: !isMinori ?
				yup.string()
				: yup
				.string()
				.trim()
				.matches(
					/^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
					"L'email inserita ha un formato non valido"
				).required('L\'email del genitore è un campo obbligatorio'),
			otherParentEmail: !isMinori || (isMinori && soleGuardianship) ?
				yup.string()
				: yup
				.string()
				.trim()
				.matches(
					/^(?!\.)(?!.*\.\.)([A-Z0-9_+-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i,
					"L'email inserita ha un formato non valido"
				).required('L\'email dell\'altro genitore è un campo obbligatorio'),
			password: isNutritionist
				? yup.string()
				: yup
					.string()
					.matches(
						/^(?=.*[a-z])(?=.*\d)[^\s]{8,}$/,
						'La password non rispetta i criteri di sicurezza'
					)
					.required('La password è un campo obbligatorio'),
			acceptConditions: yup
				.boolean()
				.isTrue('Devi accettare le condizioni per poterti registrare')
				.required(),
			marketing: yup
				.boolean(),
			soleGuardianship: yup
				.boolean(),
			phoneNumber: !isNutritionist
				? yup.string()
				: yup.string()
					.matches(
						/^\d{7,18}$/,
						'Il numero di telefono inserito non ha un formato non valido'
					)
					.required('Il numero di telefono è un campo obbligatorio'),
		}),
		onSubmit: (values) => {
			const {givenName}: any = values;
			const spaceIndex = givenName.indexOf(' ');
			if (spaceIndex === -1) {
				register({
					givenName,
					familyName: '',
					email: values.email,
					emailPartner: values.emailPartner,
					emailParent: values.emailParent,
					otherParentEmail: values.otherParentEmail,
					password: values.password,
					phoneNumber: values.phoneNumber,
					values
				});
			} else {
				const firstName = givenName.substring(0, spaceIndex);
				const lastName = givenName.substring(spaceIndex + 1);
				register({
					givenName: firstName,
					familyName: lastName,
					email: values.email,
					emailPartner: values.emailPartner,
					emailParent: values.emailParent,
					otherParentEmail: values.otherParentEmail,
					password: values.password,
					phoneNumber: values.phoneNumber,
					values
				});
			}
		}
	});

	const registerService = trpc.authService.auth.register.useMutation();
	const deleteCompletedSurvey = trpc.mainService.completedSurvey.deleteCompletedSurvey.useMutation();

	const setIubendaSubjectId = trpc.authService.auth.setIubendaSubjectId.useMutation();

  const getCurrentUserInfoResponse =
    trpc.mainService.user.getCurrentUserInfo.useQuery(undefined, {
      enabled: !!getToken("accessToken"),
  });

  useEffect(() => {
		analyticsService({ 'event': 'survey_submitted' });
    analyticsService({'event': 'virtualPageview', 'pageUrl': '/registrazione', 'pageTitle': 'Patient Register'});
  }, []);

  const register = ({
    givenName,
    familyName,
    emailPartner,
    emailParent,
		otherParentEmail,
    email,
    password,
		phoneNumber,
		values
  }: {
    givenName: string;
    familyName: string;
    email: string;
    emailPartner: string;
    emailParent: string;
		otherParentEmail: string;
    password: string;
		phoneNumber: string;
		values: typeof formik.values
  }) => {
    setRegisterError(null);
    setRegisterLoading(true);
    registerService
      .mutateAsync({
        survey: answers,
        given_name: trimString(givenName),
        family_name: trimString(familyName),
        gender: trimString(gender),
        email: trimString(email),
        partnerEmail: trimString(emailPartner) || undefined,
        parentEmail: trimString(emailParent) || undefined,
				otherParentEmail: trimString(otherParentEmail) || undefined,
        password: trimString(password),
        newsletter: newsLetter,
				marketing,
				soleGuardianship,
				phoneNumber: phoneNumber ? `${phonePrefix}${phoneNumber}` : undefined,
				subPathTherapyType: answers?.id === '4a27d3b8-50bd-4d99-a53e-fe85491d8bd3' ? 'parent_support' : undefined,

			})
			.then(async (data) => {
				if (isNutritionist) {
					setNutritionistModal(true);
				} else {
					navigate(routes.ChooseTherapist.path, {
						state: {email, campaignId: state?.campaignId}
					});
					const {tokens} = data;
					const {accessToken, refreshToken} = tokens;
					localStorage.setItem('accessToken', accessToken!);
					localStorage.setItem('refreshToken', refreshToken!);
				}
				localStorage.removeItem("surveyLogsId");
				state?.completedSurvey && await deleteCompletedSurvey.mutateAsync({id: state?.completedSurvey});
				if (!isDevEnvironment()) {
					const subjectId = await sendConsentToIubenda(trimString(email), trimString(givenName), trimString(familyName), values);
					if (subjectId) {
						await setIubendaSubjectId.mutateAsync({
							patientId: data?.userId,
							iubendaSubjectId: subjectId
						});
					}
				}
				removeTherapistData();
        analyticsService({ 'event': 'user_registered' });
			})
      .catch((e) => {
				raiseException(e);
        setRegisterLoading(false);
        if (e?.shape?.errorCode === "EmailAlreadyExists") {
          if (e?.shape?.message === "partner") {
            formik.setErrors({ emailPartner: "L'email inserita è già in uso" });
          } else if (e?.shape?.message === "parent") {
            formik.setErrors({ emailParent: "L'email inserita è già in uso" });
          } else if (e?.shape?.message === "otherParent") {
            formik.setErrors({ otherParentEmail: "L'email inserita è già in uso" });
          } else if (e?.shape?.message === "patientEmailEqualParentEmail") {
            formik.setErrors({
							email: "L'email del paziente non deve essere la stessa dell'email del genitore",
							...(email === emailParent ? { emailParent: "L'email del paziente non deve essere la stessa dell'email del genitore" } : {}),
							...(otherParentEmail && email === otherParentEmail ? { otherParentEmail: "L'email del paziente non deve essere la stessa dell'email del genitore" } : {}),
						});
          } else if (e?.shape?.message === "parentEmailEqualOtherParentEmail") {
						formik.setErrors({
							emailParent: "L'email del genitore non deve essere la stessa dell'email dell'altro genitore",
							otherParentEmail: "L'email del genitore non deve essere la stessa dell'email dell'altro genitore"
						});
          } else {
            formik.setErrors({ email: "L'email inserita è già in uso" });
          }
        } else {
          setRegisterError(
            "Si è verificato un errore inaspettato. Verifica di essere connesso a internet e riprova."
          );
        }
      });
  };

	const sendConsentToIubenda = async (email: string, firstName: string, lastName: string, values: typeof formik.values) => {
		const fullName = `${firstName} ${lastName}`.trim();

		const formContent = {
			givenName: fullName,
			email: email,
			emailPartner: values?.emailPartner,
			emailParent: values?.emailParent,
			otherParentEmail: values?.otherParentEmail,
			terms_conditions: acceptConditions,
			marketing: marketing,
			newsletter: newsLetter,
			soleGuardianship: soleGuardianship
		}

		const proofsContent = Object.fromEntries(
			Object.entries(formContent).filter(([_, value]) => value !== undefined && value !== '')
		);

		const body = {
			subject: {
				email: email,
				full_name: fullName,
				verified: false
			},
			preferences: {
				terms_conditions: acceptConditions,
				marketing: marketing,
				newsletter: newsLetter,
			},
			proofs: [
				{
					content: JSON.stringify(proofsContent),
					form: formData
				}
			],
			legal_notices: [
				{
					identifier: "privacy_policy",
					version: "1"
				}
			]
		}

		return createIubendaConsent(body);
	}

	useEffect(() => {
		if (!state) {
			navigate(routes.Survey.links.intro);
		} else {
			setAnswers(state?.answers);
		}
	}, [state]);


	const handleNutritionistModalClose = () => {
		setNutritionistModal(false);
		window.location.href = appSiteUrl || '';
		if (state?.answers?.id) state = null;
	};

	const renderBottomRow = () => {
		const bottomRow = (Icon: React.ElementType, text: React.ReactNode) => (
			<div className={styles.bottomRow}>
				<div className={styles.iconWrapper}>
					<Icon/>
				</div>
				<div className={styles.text}>{text}</div>
			</div>
		);

		if (isPsychiatry) {
			return (
				<>
					{bottomRow(CalendarIcon, 'Potrai prenotare una visita specialistica online e ricevere un supporto professionale e su misura per le tue esigenze.')}
					{bottomRow(CurrencyEuroIcon, <>La visita ha un costo di <span style={{fontWeight: 'bold'}}>150</span> euro,
						che potrai detrarre dalle tue tasse</>)}
				</>
			);
		}
		if (isCouple) {
			return bottomRow(HeartIcon, 'Create il vostro account e iniziate un percorso di crescita col terapeuta più adatto alla vostra coppia');
		}
		if (isMinori) {
			return bottomRow(HeartIcon, 'Crea il tuo account e incontra il terapeuta che abbiamo selezionato per prendersi cura di tua/o figlia/o');
		}
		if (isNutritionist) {
			return bottomRow(HeartIcon, 'Ti chiediamo l’ultimo passaggio: Per prenotare il colloquio conoscitivo gratuito con il nutrizionista, lasciaci la tua e-mail e il tuo numero di cellulare sarai ricontattato il prima possibile per fissare l’appuntamento.');
		}
		return bottomRow(HeartIcon, 'Crea il tuo account e inizia il tuo percorso di crescita col terapeuta più adatto a te');
	};

	return (
		<div className={`${styles.register} ${className}`}>
			{nutritionistModal ?
				<Modal modal subClassName="modalWithSmallHeader">
					<>
						<FormHeader
							title=""
							closeModal={handleNutritionistModalClose}
							closeButton
						></FormHeader>
						<div className={styles.nutritionistText}>
							Grazie per aver scelto Mymentis, ti metteremo presto in contatto con la nutrizionista scelta per te.
						</div>
						<img className={styles.nutritionistImage}
								 src={nutritionist}
								 alt="nutritionist"/>
						<Button className={styles.button} variant="primary"
										onClick={handleNutritionistModalClose}>Chiudi</Button>
					</>
				</Modal> :
				<>
					<img src={greenShape} alt="green shape" className={styles.greenShape}/>
					<img
						src={purpleShape}
						alt="purple shape"
						className={styles.purpleShape}
					/>
					<div className={styles.registerCard}>
						<div className={styles.instruction}>
							<div className={styles.topPart}>
								{isCouple ?
									<span>Registratevi ora, il primo incontro è gratuito</span>
									:
									isMinori ?
										<span>Registrati ora, il primo incontro è gratuito</span>
										: isPsychiatry ?
											<span>Registrati e conosci il tuo psichiatra</span> :
											isNutritionist ?
												<span>È il momento di conoscere il tuo nutrizionista</span> :
												<span>Registrati ora, il primo incontro è gratuito</span>
								}
							</div>
							<div className={styles.bottomPart}>
								{renderBottomRow()}
								<img className={styles.questionCardImage}
										 src={isCouple ? supporto : selfLove}
										 alt="percorsoPersonalizzato"/>
							</div>
						</div>
						<form onSubmit={formik.handleSubmit} ref={formRef} className={styles.form} id="registerForm">
							{!isNutritionist && (
								<h5 className={styles.formTitle}>
									{`L'ultimo step prima di conoscere il ${isCouple ? 'vostro' : 'tuo'} ${isPsychiatry ? 'psichiatra' : 'terapeuta'}`}
								</h5>
							)}
							<div className={styles.inputsWrapper}>
								<div className={styles.textInputWrapper}>
									<Input
										placeholder="Nome reale o di fantasia"
										label="Nome*"
										name="givenName"
										checkboxId="registrationName"
										value={formik.values.givenName}
										onChange={formik.handleChange}
										error={
											(formik.errors.givenName &&
												formik.touched.givenName &&
												formik.errors.givenName) as string
										}
									/>
									<Input
										placeholder="Email"
										label="Email*"
										type="email"
										name="email"
										checkboxId="registrationEmail"
										value={formik.values.email}
										onChange={formik.handleChange}
										error={
											(formik.errors.email &&
												formik.touched.email &&
												formik.errors.email) as string
										}
									/>
									{isNutritionist && (
										<div className={styles.phoneInputWrapper}>
											<div className={styles.phoneInput}>
												<Input
													select
													options={internationalPrefix.map((p) => ({
														name: p.prefix,
														value: p.prefix,
													}))}
													placeholder="+39"
													value={phonePrefix}
													onChange={(e) => setPhonePrefix(e.target.value)}
												/>
												<Input
													placeholder="3471234567"
													name="phoneNumber"
													value={phoneNumber}
													onChange={(e) => {
														setPhoneNumber(e.target.value);
														formik.handleChange(e);
													}}
													error={
														formik.errors.phoneNumber && formik.touched.phoneNumber
															? formik.errors.phoneNumber
															: ''
													}
												/>
											</div>
										</div>
									)}
									{isCouple && (
										<Input
											placeholder="Email partner"
											label="Email partner"
											type="email"
											name="emailPartner"
											value={formik.values.emailPartner}
											onChange={formik.handleChange}
											error={
												(formik.errors.emailPartner &&
													formik.touched.emailPartner &&
													formik.errors.emailPartner) as string
											}
										/>
									)}
									{isMinori && (
										<div className={styles.textInputWrapper}>
											<Input
												placeholder="Email"
												label="Email Genitore 1 / tutore 1 / curatore legale 1"
												type="email"
												name="emailParent"
												value={formik.values.emailParent}
												onChange={formik.handleChange}
												error={
													(formik.errors.emailParent &&
														formik.touched.emailParent &&
														formik.errors.emailParent) as string
												}
											/>
											{!soleGuardianship && (
												<Input
													placeholder="Email"
													label="Email Genitore 2 / tutore 2 / curatore legale 2"
													type="email"
													name="otherParentEmail"
													value={formik.values.otherParentEmail}
													onChange={formik.handleChange}
													error={
														(formik.errors.otherParentEmail &&
															formik.touched.otherParentEmail &&
															formik.errors.otherParentEmail) as string
													}
												/>
											)}

											<div className={styles.checkboxWrapper}>
												<Input
													checked={soleGuardianship}
													className={styles.checkbox}
													onChange={(e) => {
														formik.setValues({
															...formik.values,
															soleGuardianship: e.target.checked,
														});
														setSoleGuardianship(e.target.checked);
													}}
													type="checkbox"
													name="soleGuardianship"
													checkboxId="soleGuardianship"
													checkboxLabel={
														<div className={styles.checkboxText}>
															Dichiaro, ai sensi dell’art. 1.3 delle{' '}
															<span
																onClick={() => window.open(appSiteUrl + routes.TermsAndCondition.path)}
																style={{
																	color: '#2C2C4B',
																	textDecoration: 'underline',
																	cursor: 'pointer',
																}}
															>
														      condizioni generali del servizio
														    </span>
															, di essere l’unico soggetto legalmente titolato a esercitare la potestà genitoriale /
															amministrare la tutela / esercitare la curatela e pertanto a esprimere l’accettazione
															delle suddette Condizioni.
														</div>
													}
												/>
											</div>
										</div>
									)}
									{!isNutritionist && (
										<Input
											className={styles.passwordInput}
											type={passwordTypeToggle ? 'password' : 'text'}
											name="password"
											label="Password*"
											value={formik.values.password}
											onChange={formik.handleChange}
											error={
												(formik.errors.password &&
													formik.touched.password &&
													formik.errors.password) as string
											}
											placeholder="Password"
											rightIcon={
												<p
													className={styles.showPw}
													onClick={() => {
														setPasswordTypeToggle(!passwordTypeToggle);
													}}
												>
													{passwordTypeToggle ? 'Mostra' : 'Nascondi'}
												</p>
											}
										/>
									)}
								</div>
								<div className={styles.checkboxWrapper}>
									<Input
										checked={marketing}
										className={styles.checkbox}
										onChange={(e) => {
											formik.setValues({
												...formik.values,
												marketing: e.target.checked,
											});
											setMarketing(e.target.checked);
										}}
										type="checkbox"
										name="marketing"
										checkboxId="marketing"
										checkboxLabel={
											<div className={styles.checkboxText}>
												Vorrei ricevere comunicazioni promozionali ai sensi del punto 3.11 della{' '}
												<span
													onClick={() => window.open(`${generalPrivacyLink}#311`, '_blank')}
													style={{
														color: '#2C2C4B',
														textDecoration: 'underline',
														cursor: 'pointer',
													}}
												>
                      Privacy Policy
                    </span>
											</div>
										}
									/>
									<Input
										onChange={(e) => {
											setNewsLetter(e.target.checked);
										}}
										className={styles.checkbox}
										type="checkbox"
										checkboxId="newsletter"
										name="newsletter"
										checkboxLabel={
											<div className={styles.checkboxText}>
												Vorrei ricevere newsletter e comunicazioni informative ai sensi del punto 3.10 della{' '}
												<span
													onClick={() => window.open(`${generalPrivacyLink}#310`, '_blank')}
													style={{
														color: '#2C2C4B',
														textDecoration: 'underline',
														cursor: 'pointer',
													}}
												>
                      Privacy Policy
                    </span>
											</div>
										}
									/>
									<Input
										checked={acceptConditions}
										className={styles.checkbox}
										onChange={(e) => {
											formik.setValues({
												...formik.values,
												acceptConditions: e.target.checked,
											});
											setAcceptConditions(e.target.checked);
										}}
										type="checkbox"
										name="termsAndConditions"
										checkboxId="termsAndConditions"
										error={
											(formik.errors.acceptConditions &&
												formik.touched.acceptConditions &&
												formik.errors.acceptConditions) as string
										}
										checkboxLabel={
											<div className={styles.checkboxText}>
												Ho letto e accetto i termini e le{' '}
												<span
													onClick={() => window.open(appSiteUrl + routes.TermsAndCondition.path)}
													style={{
														color: '#2C2C4B',
														textDecoration: 'underline',
														cursor: 'pointer',
													}}
												>
                      condizioni generali del servizio
                    </span>
												{' '}e la {' '}
												<span
													onClick={() => window.open(`${privacyLink}`, '_blank')}
													style={{
														color: '#2C2C4B',
														textDecoration: 'underline',
														cursor: 'pointer',
													}}
												>
                      Privacy Policy*
                    </span>
											</div>
										}
									/>
								</div>
								{registerError && (
									<Alert
										className={styles.registerError}
										type="error"
										message={registerError}
										closable
									/>
								)}
								<Button
									className={styles.btn}
									disabled={registerLoading}
									type="submit"
									id="submitButton"
								>
									Registrati
								</Button>
							</div>
							{!isNutritionist && (
								<div className={styles.login}>
									<div className={styles.spacer}></div>
									<div>
										Hai già un profilo?
										<span
											onClick={async () => {
												const accessToken = getToken('accessToken');
												const result = await getCurrentUserInfoResponse.refetch();
												if (accessToken) {
													if (result.data?.user?.role === 'therapist') {
														return navigate(routes.Chat.path);
													}

													if (!result.data?.user?.therapistId) {
														navigate(routes.ChooseTherapist.path, {
															...(result.data?.user?.emailVerified
																? {}
																: {
																	state: result.data?.user?.email,
																}),
														});
													} else {
														navigate(routes.Chat.path);
													}
												} else {
													navigate(routes.Login.path);
												}
											}}
											style={{
												color: '$primary-1_dark',
												fontWeight: 600,
												cursor: 'pointer',
											}}
										>
               	 Accedi</span>
									</div>
								</div>
							)}
						</form>
					</div>
				</>
			}
		</div>
	);
};
